import { Box, Dialog, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import Icon from '../../components/Icon';
import WarningGlyph from '../../components/WarningGlyph';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { formatDuration, type ModeOption, type TravelMode } from './travel';
import { formatClock } from './dateTime';
import { TRAVEL_STROKE } from './travelStyle';

/**
 * "How are you getting there?"
 *
 * Replaces a click that used to leave for Google Maps immediately. Leaving was
 * the wrong default: the useful question at a travel block is not "show me the
 * route" but "is there a faster way", and answering it took a round trip to
 * another tab and back. Every mode's real cost is on screen here, changing the
 * mode re-times the block behind the dialog, and the Maps link is still one
 * click away for anyone who wants the actual directions.
 */

/**
 * The departure moves in quarter-hours, as the block itself does, except at
 * the two ends of its range. Leaving exactly in time to arrive on time is
 * rarely on the quarter-hour (a 1 hr 56 min journey to a four o'clock is a
 * 2:04 departure), and a stepper that only stopped on quarter-hours would halt
 * at 2:15 with no way to be on time. So the last step before a limit lands on
 * the limit.
 */
const DEPARTURE_STEP = 15;

/**
 * When you leave, for a screen that cannot drag the block.
 *
 * The travel block is moved by dragging it on a desktop, which is quick and
 * says a lot — you can see the slack close up as you go. A phone has no room
 * for that gesture, and the same decision is two taps here instead, with the
 * arrival spelled out because that is the number the departure is chosen for.
 */
const DepartureRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 14,
  padding: '10px 12px',
  borderRadius: 12,
  background: 'rgba(243,238,231,0.06)',
  border: '1px solid rgba(243,238,231,0.16)',
});

const Step = styled('button')({
  appearance: 'none',
  width: 34,
  height: 34,
  borderRadius: '50%',
  border: '1px solid rgba(243,238,231,0.42)',
  background: 'transparent',
  color: BRAND.parchment,
  fontSize: 17,
  lineHeight: 1,
  cursor: 'pointer',
  flexShrink: 0,
  '&:hover:not(:disabled)': { borderColor: BRAND.parchment },
  '&:disabled': { opacity: 0.25, cursor: 'default' },
});

const StepValue = styled('span')({
  minWidth: 72,
  textAlign: 'center',
  fontFamily: FONTS.body,
  fontSize: 13.5,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
});

const Panel = styled('div')({
  background: BRAND.mochaDeep,
  color: BRAND.parchment,
  padding: '22px 24px 18px',
});

const Choice = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on: boolean }>(
  ({ $on }) => ({
    appearance: 'none',
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 14px',
    borderRadius: 10,
    textAlign: 'left',
    fontFamily: FONTS.body,
    background: $on ? 'rgba(173,111,124,0.5)' : 'transparent',
    color: BRAND.parchment,
    border: `1.5px solid ${$on ? TRAVEL_STROKE : 'rgba(243,238,231,0.28)'}`,
    transition: 'background 120ms ease, border-color 120ms ease',
    '&:hover': { borderColor: TRAVEL_STROKE },
  }),
);

const Note = styled('p')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 7,
  margin: '14px 0 0',
  fontFamily: FONTS.body,
  fontSize: 11.5,
  lineHeight: 1.5,
  color: 'rgba(243,238,231,0.78)',
  '& svg': { flexShrink: 0, marginTop: 2, color: '#ef3d23' },
  '& strong': { color: BRAND.parchment, fontWeight: 700 },
});

const MapsLink = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  marginTop: 14,
  fontFamily: FONTS.body,
  fontSize: 12.5,
  fontWeight: 700,
  color: BRAND.parchment,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  textDecorationColor: 'rgba(243,238,231,0.45)',
  '&:hover': { textDecorationColor: BRAND.parchment },
});

/** Sentence case, since these head a row rather than sit mid-sentence. */
const LABEL: Record<TravelMode, string> = {
  walk: 'Walking',
  bike: 'Biking',
  transit: 'Transit',
  ferry: 'Ferry',
};

interface Props {
  open: boolean;
  from: string;
  to: string;
  /** Titles of the events with no street address. Empty when both have one. */
  unlocated: string[];
  options: ModeOption[];
  selected: TravelMode;
  /** Minutes the window allows. Used only to mark options that will not fit. */
  windowMinutes: number;
  mapsUrl: string | null;
  onSelect: (mode: TravelMode) => void;
  /**
   * When you leave, and the range it can be moved through.
   *
   * Present on a phone, where the block cannot be dragged: the same departure
   * is set here in quarter-hours instead. Absent everywhere else, where
   * dragging the block is both quicker and more expressive.
   */
  departure?: {
    startMinutes: number;
    earliest: number;
    latest: number;
    /** How long the journey takes, so the arrival can be spelled out. */
    minutes: number;
    onChange: (startMinutes: number) => void;
  };
  onClose: () => void;
}

export default function TravelModeDialog({
  open,
  from,
  to,
  unlocated,
  options,
  selected,
  windowMinutes,
  departure,
  mapsUrl,
  onSelect,
  onClose,
}: Props) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: { sx: { background: BRAND.mochaDeep, backgroundImage: 'none', borderRadius: 3 } },
      }}
    >
      <Panel>
        <Typography
          sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.13em', opacity: 0.55, mb: 0.6 }}
        >
          HOW ARE YOU GETTING THERE?
        </Typography>
        <Typography sx={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 18, mb: 2 }}>
          {from} → {to}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {options.map(({ mode, minutes }) => {
            const overruns = minutes > windowMinutes;
            return (
              <Choice
                key={mode}
                $on={mode === selected}
                aria-pressed={mode === selected}
                onClick={() => onSelect(mode)}
              >
                <Icon name={`transport:${mode}`} size={22} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{LABEL[mode]}</Typography>
                  {/* The window is the fixed thing here, so an option that
                      cannot fit it is called out rather than left to arithmetic. */}
                  {overruns && (
                    <Typography sx={{ fontSize: 11, opacity: 0.72 }}>
                      {formatDuration(minutes - windowMinutes)} more than you have
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {formatDuration(minutes)}
                </Typography>
              </Choice>
            );
          })}
        </Box>

        {departure && (
          <DepartureRow>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>I leave at</Typography>
              <Typography sx={{ fontSize: 11.5, opacity: 0.7 }}>
                Arriving {formatClock(departure.startMinutes + departure.minutes)}
              </Typography>
            </Box>
            <Step
              aria-label="Leave earlier"
              disabled={departure.startMinutes <= departure.earliest}
              onClick={() =>
                departure.onChange(Math.max(departure.startMinutes - DEPARTURE_STEP, departure.earliest))
              }
            >
              −
            </Step>
            <StepValue>{formatClock(departure.startMinutes)}</StepValue>
            <Step
              aria-label="Leave later"
              disabled={departure.startMinutes >= departure.latest}
              onClick={() =>
                departure.onChange(Math.min(departure.startMinutes + DEPARTURE_STEP, departure.latest))
              }
            >
              +
            </Step>
          </DepartureRow>
        )}

        {mapsUrl && (
          <MapsLink href={mapsUrl} target="_blank" rel="noopener noreferrer">
            Preview in Google Maps
            <Icon name="ui:arrowUpRight" size={11} />
          </MapsLink>
        )}

        {/* Two different caveats, and only the second is actionable. Every
            estimate is neighborhood to neighborhood; but when a venue has
            published no address, the neighborhood is *all* there is, and the
            real journey could be twenty minutes either side of this. That one
            names the events, because the fix is to go and read their listings. */}
        {unlocated.length > 0 && (
          <Note>
            <WarningGlyph size={13} />
            <span>
              Measured between neighborhood centres, because{' '}
              {unlocated.length === 1 ? (
                <strong>{unlocated[0]}</strong>
              ) : (
                <>
                  <strong>{unlocated[0]}</strong> and <strong>{unlocated[1]}</strong>
                </>
              )}{' '}
              {unlocated.length === 1 ? 'has' : 'have'} published no street address. Check the
              listing before you plan around these times — the real journey could be well either
              side of them.
            </span>
          </Note>
        )}

        <Typography sx={{ fontSize: 11, opacity: 0.5, mt: 1.4, lineHeight: 1.5 }}>
          Neighborhood-to-neighborhood estimates, not routed directions.
          {!departure && ' Drag the block to change when you leave.'}
        </Typography>
      </Panel>
    </Dialog>
  );
}
